/**
 * ID Obfuscator Utility
 *
 * Encodes string or numeric IDs into different-looking strings and decodes them back.
 * This is for URL obfuscation only - NOT cryptographic security.
 *
 * Example:
 *   encode("3b275d195cd0...") → "fxXk7mNp2qRs9fTvWx..."
 *   encode(123) → "fxABC123..."
 *   decode("fxXk7mNp2qRs9fTvWx...") → "3b275d195cd0..."
 */

export class IdObfuscator {
  // Secret key for XOR operation (change this to your own value)
  private static readonly SECRET_KEY = 'F0xL1n3_S3cr3t_K3y_2024!';

  // Prefix to identify obfuscated IDs
  private static readonly PREFIX = 'fx';

  /**
   * Encode a string or numeric ID into an obfuscated string
   */
  static encode(id: string | number): string {
    if (id === null || id === undefined || id === '') {
      throw new Error('ID cannot be empty');
    }

    // Convert to string if numeric
    const idStr = String(id);

    // XOR each character with the secret key
    let xored = '';
    for (let i = 0; i < idStr.length; i++) {
      const charCode = idStr.charCodeAt(i) ^ IdObfuscator.SECRET_KEY.charCodeAt(i % IdObfuscator.SECRET_KEY.length);
      xored += String.fromCharCode(charCode);
    }

    // Convert to Base64 and make URL-safe
    const base64 = btoa(xored)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    // Reverse the string and add prefix
    const reversed = base64.split('').reverse().join('');

    return IdObfuscator.PREFIX + reversed;
  }

  /**
   * Decode an obfuscated string back to the original ID
   * Returns null if the string is invalid
   */
  static decode(encoded: string): string | null {
    if (!encoded) {
      return null;
    }

    try {
      // Check and remove prefix
      if (!encoded.startsWith(IdObfuscator.PREFIX)) {
        // Not an obfuscated ID - return as-is (backwards compatibility)
        return encoded;
      }

      const withoutPrefix = encoded.substring(IdObfuscator.PREFIX.length);

      // Reverse the string
      const reversed = withoutPrefix.split('').reverse().join('');

      // Restore Base64 padding and characters
      let base64 = reversed
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      // Add padding if needed
      while (base64.length % 4 !== 0) {
        base64 += '=';
      }

      // Decode Base64
      const xored = atob(base64);

      // XOR back to get original
      let original = '';
      for (let i = 0; i < xored.length; i++) {
        const charCode = xored.charCodeAt(i) ^ IdObfuscator.SECRET_KEY.charCodeAt(i % IdObfuscator.SECRET_KEY.length);
        original += String.fromCharCode(charCode);
      }

      return original;
    } catch {
      return null;
    }
  }

  /**
   * Check if a string is an obfuscated ID (starts with prefix)
   */
  static isObfuscated(value: string): boolean {
    if (!value) return false;
    return value.startsWith(IdObfuscator.PREFIX);
  }
}

